/**
 * LOUPE — the pinned detail drawer.
 *
 * WHY NOT A MODAL. WALL opened tile detail in an overlay, and operate.md is
 * blunt about that: "modals are usually laziness — exhaust inline / progressive
 * alternatives first", and a modal is only earned by a task needing
 * interruption or protected focus. Reading a lane's numbers needs neither. Worse,
 * an overlay covers the other two pipelines, which is the one thing this board
 * exists to prevent: it was built because a sink went unwritten for 11 days
 * while every panel still rendered, and an overlay reintroduces exactly that
 * blindness for as long as it is open.
 *
 * So the drawer is pinned beside the strips, not over them. The name is literal:
 * a loupe on a contact sheet inspects one frame without lifting it off the
 * sheet. The strips stay on screen, keep their channel hue, and keep alarming.
 *
 * It is presentational only — every figure arrives already resolved, so this
 * file has no hooks and cannot disagree with the strip that opened it.
 */
import { useEffect, useRef } from 'react'

export type LoupeRow = {
  k: string
  /** null renders the cannot-state register, never a zero. */
  v: string | null
  /** Stream names and other paste-able identifiers set in mono. */
  mono?: boolean | undefined
}

export type LoupeDetail = {
  title: string
  /** Pipeline id, so the drawer carries the same channel hue as its strip. */
  channel: 'collector' | 'classifier' | 'vidiq'
  rows: LoupeRow[]
  /** The mechanism — why these figures are the ones that matter here. */
  why?: string | undefined
}

export function Loupe({ detail, onClose }: { detail: LoupeDetail; onClose: () => void }) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  /* Focus moves in so a keyboard reader lands on the detail they just opened.
     Deliberately NOT a focus trap: the drawer is pinned, not modal, and trapping
     focus inside a non-modal panel would strand a keyboard user away from the
     two strips still reporting behind it. */
  useEffect(() => { ref.current?.focus() }, [detail.title])

  return (
    <aside className={`loupe loupe-${detail.channel}`} ref={ref} tabIndex={-1}
           aria-label={`${detail.title} detail`}>
      <header className="loupe-h">
        <span className="loupe-t">{detail.title}</span>
        <button className="loupe-x" onClick={onClose} aria-label="Close detail">
          Close
        </button>
      </header>
      <dl className="loupe-rows">
        {detail.rows.map(r => (
          <div key={r.k}>
            <dt className={r.mono ? 'mono' : undefined}>{r.k}</dt>
            <dd className="tnum">
              {r.v === null
                ? <span className="unstated" title="the server cannot state this yet">
                    not stated
                  </span>
                : r.v}
            </dd>
          </div>
        ))}
      </dl>
      {detail.why ? <p className="loupe-why">{detail.why}</p> : null}
    </aside>
  )
}
